/**
 * A drop's time: its minute on the clock, or its own pinned minute — the
 * pin opens "On the clock | Pinned" with the exact time, the country's time
 * on the same line (19/10/2026). A drop past the
 * container's end says so; one outside the country's window carries a small
 * chip that opens "Send it anyway" (14/10/2026 — the window is a hard limit,
 * lifted on purpose for one drop, or for the whole container in Schedule; a
 * route test's drops go before the window on purpose — 20/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, Clock, Moon, Pin } from 'lucide-react';

import { Badge, Button, SegmentedPills, TimeIntervalPicker } from '@/components/ui';
import { cn } from '@/lib/utils';

import { dkLabel } from '../../../../../utils/clock/dk-label';
import { dkMinOf } from '../../../../../utils/clock/today-clock';
import { hhmmToMinutes, minutesLabel, minutesToHHMM } from '../../../../../utils/format';
import type { SlotTime } from '../../../../../utils/slot-times';
import { ClockLabel } from '../../../../shared/time/ClockLabel';
import { useCountryClock } from '../../../../shared/time/country-clock-context';
import { CountryEcho } from '../../../../shared/time/CountryEcho';
import { CapChip, type CapMark } from '../cap/CapChip';

const MODES = [
  { value: 'clock', label: 'On the clock' },
  { value: 'pinned', label: 'Pinned' },
] as const;

interface Props {
  pinnedAtMin: number | null;
  time: SlotTime | undefined;
  enabled: boolean;
  position: number;
  onPin: (pinnedAtMin: number | null) => void;
  /** This drop's own "send it outside the window". */
  sendOutside: boolean;
  onSendOutside: (send: boolean) => void;
  /** 20/10/2026 — the daily SMS cap cut this drop, or clamped it to `capAmount`. */
  cap?: CapMark | null;
  capAmount?: number | null;
}

export const DropTime: FC<Props> = ({ pinnedAtMin, time, enabled, position, onPin, sendOutside, onSendOutside, cap = null, capAmount = null }) => {
  const pinned = pinnedAtMin !== null;
  const { tz, runDate } = useCountryClock(); // a pin is a Danish time (15/10/2026), seeded from the drop's planned minute
  // Danish first: the drop's planned minute on the Danish clock; its country time follows.
  const shown = enabled && time ? (dkLabel(time.minute, tz, runDate) ?? minutesLabel(time.minute)) : '—';
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="flex shrink-0 items-center gap-1">
        <span className={`whitespace-nowrap font-mono text-xs tabular-nums ${pinned ? 'font-semibold text-warning' : ''}`}><ClockLabel label={shown} /></span>
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              aria-label={pinned ? `Drop ${position}: pinned time — change` : `Drop ${position}: pin its time`}
              className={`rounded p-0.5 transition-colors ${pinned ? 'bg-warning/10 text-warning' : 'text-muted-foreground/50 hover:bg-primary/10 hover:text-primary'}`}
            >
              <Pin className="h-3 w-3" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content sideOffset={6} align="start" className="z-50 flex w-64 flex-col gap-2.5 rounded-lg border bg-popover p-3 text-sm shadow-lg animate-in fade-in-0 zoom-in-95">
              <span className="text-xs font-semibold">Time of this drop</span>
              <SegmentedPills
                options={MODES}
                value={pinned ? 'pinned' : 'clock'}
                onChange={(v) => onPin(v === 'pinned' ? dkMinOf(time?.minute ?? 720, tz, runDate) : null)}
              />
              {pinned ? (
                <span className="flex items-center gap-2">
                  <span aria-hidden title="Danish time">🇩🇰</span>
                  <TimeIntervalPicker value={minutesToHHMM(pinnedAtMin)} onChange={(v) => onPin(hhmmToMinutes(v) ?? pinnedAtMin)} />
                  <CountryEcho min={time?.minute ?? null} />
                </span>
              ) : null}
              <p className="text-[11px] leading-snug text-muted-foreground">
                {pinned ? (
                  <>A pinned drop keeps its Danish time wherever the others move.</>
                ) : (
                  <><Clock className="mr-1 inline h-3 w-3" />It takes its turn on the container&apos;s clock, in the order you drag it.</>
                )}
              </p>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
        {time?.clamped ? <Badge tone="warning">past end</Badge> : null}
      </span>
      {/* 19/10/2026 — the country's time on the same line: one column, one line; the day marks are small
          raised tags so the 🌙 chip stays inside the cell (20/10/2026 — it slid under the group chips). */}
      <span className="flex shrink-0 items-center gap-1 whitespace-nowrap">
        {enabled && time ? <CountryEcho min={time.minute} className="whitespace-nowrap" compact /> : null}
        {enabled && (time?.outsideWindow || time?.sentOutside) ? (
          <WindowChip sent={time.sentOutside === true} own={sendOutside} test={time.routeTest === true} position={position} onSend={onSendOutside} />
        ) : null}
        {enabled && cap ? <CapChip mark={cap} position={position} plannedAmount={capAmount} /> : null}
      </span>
    </span>
  );
};

/** Outside the window: not sent (amber) or sent anyway — one line, the choice behind a click. */
type ChipProps = { sent: boolean; own: boolean; test: boolean; position: number; onSend: (send: boolean) => void };
const WindowChip: FC<ChipProps> = ({ sent, own, test, position, onSend }) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <button
        type="button"
        aria-label={`Drop ${position}: outside the window — ${sent ? 'sent' : 'not sent'}`}
        title={sent ? 'Outside the window — sent anyway' : 'Outside the window — not sent'}
        className={cn(
          'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-px text-[10px] font-medium leading-4 transition-colors',
          sent ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15' : 'border-warning/40 bg-warning/10 text-warning hover:bg-warning/20',
        )}
      >
        <Moon className="h-2.5 w-2.5" aria-hidden />
        {sent ? <Check className="h-2.5 w-2.5" aria-hidden /> : null}
      </button>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content sideOffset={6} align="start" className="z-50 flex w-64 flex-col gap-2 rounded-lg border bg-popover p-3 text-xs shadow-lg animate-in fade-in-0 zoom-in-95">
        <span className="font-semibold">{sent ? 'Sent outside the window' : 'Outside the window — not sent'}</span>
        <p className="leading-snug text-muted-foreground">
          {test
            ? 'A route test’s drops go before the window opens, so the day starts on the best route.'
            : sent && !own
            ? 'The whole container sends outside the window (Schedule › Outside the window).'
            : sent
              ? 'This drop goes even though it is outside the country’s window.'
              : 'The country’s window is a hard limit. Let this one drop go anyway, or the whole container in Schedule › Outside the window.'}
        </p>
        {(!sent || own) && !test ? (
          <Button size="sm" variant={sent ? 'outline' : 'default'} className="h-7 self-start text-xs" onClick={() => onSend(!sent)}>
            {sent ? 'Don’t send it' : 'Send it anyway'}
          </Button>
        ) : null}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
);
